import {
  AlertTriangle,
  CheckCircle2,
  Info,
  Loader2,
  XCircle,
} from 'lucide-react';
import { Toaster as SonnerToaster, toast as sonnerToast } from 'sonner';

/**
 * TOASTS
 *
 * Built on sonner, which already handles stacking, timing, swipe-to-dismiss
 * and the screen-reader live region. What this adds is a semantic API, so a
 * success toast looks the same everywhere instead of each call site picking an
 * icon and a colour.
 *
 * Mount <Toaster /> once, near the root of the app.
 */

export interface ToastOptions {
  /** Second line, for detail that doesn't fit in the title. */
  description?: string;
  /** Milliseconds on screen. Errors default to staying until dismissed. */
  duration?: number;
  action?: { label: string; onClick: () => void };
}

const iconProps = { className: 'size-icon-md shrink-0' };

/**
 * Show a toast.
 *
 *   toast.success('Call completed');
 *   toast.error('Could not save', { description: 'Check your connection' });
 *   toast.promise(save(), { loading: 'Saving…', success: 'Saved', error: 'Failed' });
 */
export const toast = {
  success(message: string, options?: ToastOptions) {
    return sonnerToast.success(message, {
      icon: (
        <CheckCircle2 {...iconProps} className="size-icon-md text-success" />
      ),
      ...options,
    });
  },

  /**
   * Errors persist until dismissed by default. An error that vanishes after
   * four seconds is an error the user never read. So each carries a close
   * button: without one it could only be swiped away, which nobody tries,
   * and on the phone a failed checklist left its toast up for good.
   */
  error(message: string, options?: ToastOptions) {
    return sonnerToast.error(message, {
      icon: (
        <XCircle {...iconProps} className="size-icon-md text-destructive" />
      ),
      duration: Infinity,
      closeButton: true,
      ...options,
    });
  },

  warning(message: string, options?: ToastOptions) {
    return sonnerToast.warning(message, {
      icon: (
        <AlertTriangle {...iconProps} className="size-icon-md text-warning" />
      ),
      ...options,
    });
  },

  info(message: string, options?: ToastOptions) {
    return sonnerToast.info(message, {
      icon: <Info {...iconProps} className="size-icon-md text-info" />,
      ...options,
    });
  },

  loading(message: string, options?: ToastOptions) {
    return sonnerToast.loading(message, {
      icon: (
        <Loader2
          {...iconProps}
          className="size-icon-md animate-spin text-muted-foreground"
        />
      ),
      ...options,
    });
  },

  /** Ties a toast to a promise: loading, then success or error. */
  promise: sonnerToast.promise,

  dismiss: sonnerToast.dismiss,
};

/**
 * The card a toast is drawn on. Given per type rather than to every toast:
 * `toast.custom` has no type, and its content (a ToastCard) is already the
 * card, so a second one around it drew the welcome toast as a card in a card.
 */
const toastSurface =
  'rounded-lg border border-border bg-popover p-4 text-popover-foreground shadow-e3';

/**
 * The phone's toast, as the Figma's created frame (143:10835) draws it: one
 * line in a tinted pill, centred just above the tab bar where the
 * active-call pill rests, with no icon and no card. It says a thing was
 * done; the screen behind it shows the thing. The tint is the toast's kind:
 * every kind was the green of "done", so a failure read as a success.
 */
const phonePill = 'flex items-center gap-2 rounded-pill px-4 py-2 shadow-float';
const phoneSurface = {
  success: `${phonePill} bg-success-subtle text-success-subtle-foreground`,
  error: `${phonePill} bg-destructive-subtle text-destructive-subtle-foreground`,
  warning: `${phonePill} bg-warning-subtle text-warning-subtle-foreground`,
  info: `${phonePill} bg-info-subtle text-info-subtle-foreground`,
};
// Clear of the tab bar: 56px of pill over the home indicator, then 16px.
const phoneOffset =
  'calc(max(env(safe-area-inset-bottom), 1rem) + 3.5rem + 1rem)';

/**
 * Mount once near the root of the app.
 *
 * Styling comes from tokens via `toastOptions.classNames` rather than sonner's
 * own theme, so toasts match the rest of the product and follow dark mode.
 * `placement="phone"` is the mobile shell's: a pill above the tab bar.
 */
export function Toaster({
  position = 'bottom-right',
  placement = 'desk',
}: {
  position?: React.ComponentProps<typeof SonnerToaster>['position'];
  placement?: 'desk' | 'phone';
}) {
  if (placement === 'phone')
    return (
      <SonnerToaster
        position="bottom-center"
        offset={{ bottom: phoneOffset }}
        // Edge to edge: sonner's phone layout makes the stack 100% wide and
        // then starts it at the left offset, 16px by default, which pushed
        // every toast 16px right of centre.
        mobileOffset={{ bottom: phoneOffset, left: 0, right: 0 }}
        toastOptions={{
          unstyled: true,
          classNames: {
            // Pinned at both edges, as wide as its words, and centred by its
            // margins. Sonner sets a toast's width on the phone, and on a
            // wider screen leaves its sides unset, where the margins centre
            // nothing; both beat a plain class, hence the !.
            toast: '!left-0 !right-0 !mx-auto !w-fit max-w-[calc(100%-2rem)]',
            ...phoneSurface,
            title: 'font-sans text-body-lg-strong',
            description: 'font-sans text-body-sm',
            icon: 'hidden',
            closeButton:
              'order-last -mr-1 flex size-6 shrink-0 items-center justify-center rounded-pill hover:bg-foreground/5 [&_svg]:size-icon-sm',
          },
        }}
      />
    );

  return (
    <SonnerToaster
      position={position}
      // sonner's built-in themes are opted out of so nothing bypasses tokens.
      toastOptions={{
        unstyled: true,
        classNames: {
          toast: 'flex w-full items-start gap-3',
          success: toastSurface,
          error: toastSurface,
          warning: toastSurface,
          info: toastSurface,
          loading: toastSurface,
          title: 'font-sans text-body font-medium',
          description: 'font-sans text-body-sm text-muted-foreground',
          actionButton:
            'ml-auto rounded-md bg-primary px-2.5 py-1 font-sans text-body-sm font-medium text-primary-foreground',
          cancelButton:
            'ml-auto rounded-md bg-muted px-2.5 py-1 font-sans text-body-sm font-medium text-muted-foreground',
          closeButton:
            'order-last ml-auto flex size-6 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted [&_svg]:size-icon-sm',
        },
      }}
    />
  );
}
